import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { ArrowLeftIcon, CalendarIcon, UsersIcon, CurrencyDollarIcon, ClockIcon } from '@heroicons/react/24/outline'
import LoadingSpinner from '../components/LoadingSpinner'
import PoolsApi from '../services/poolsApi'
import { isPoolAdmin } from '../utils/roles'

const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'https://api.midwestrailbirds.com/v1'

interface Pool {
  poolId: string
  name: string
  description: string
  type: string
  status: 'active' | 'completed' | 'cancelled'
  startDate: string
  endDate?: string
  currentParticipants: number
  maxParticipants: number
  entryFee: number
  pickDeadline?: string
  createdAt: string
  createdBy: string
}

export default function EditPoolPage() {
  const { poolId } = useParams<{ poolId: string }>()
  const navigate = useNavigate()
  const auth0 = useAuth0()
  const { user, getAccessTokenSilently } = auth0
  
  const [pool, setPool] = useState<Pool | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  
  // Form fields
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [status, setStatus] = useState<'active' | 'completed' | 'cancelled'>('active')
  const [endDate, setEndDate] = useState('')
  const [maxParticipants, setMaxParticipants] = useState(100)
  const [entryFee, setEntryFee] = useState(0)
  const [pickDeadline, setPickDeadline] = useState('')

  const userIsPoolAdmin = isPoolAdmin(user)
  const poolsApi = new PoolsApi(auth0)

  useEffect(() => {
    if (poolId && userIsPoolAdmin) {
      fetchPool()
    }
  }, [poolId, userIsPoolAdmin])

  const fetchPool = async () => {
    try {
      setLoading(true)
      const token = await getAccessTokenSilently()
      
      const response = await fetch(`${API_BASE_URL}/pools/${poolId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch pool: ${response.statusText}`)
      }

      const data = await response.json()
      const poolData = data.pool
      
      setPool(poolData)
      
      // Populate form fields
      setName(poolData.name || '')
      setDescription(poolData.description || '')
      setStatus(poolData.status || 'active')
      setEndDate(poolData.endDate ? poolData.endDate.split('T')[0] : '') // Convert to date input format
      setMaxParticipants(poolData.maxParticipants || 100)
      setEntryFee(poolData.entryFee || 0)
      setPickDeadline(poolData.pickDeadline ? new Date(poolData.pickDeadline).toISOString().slice(0, 16) : '') // Convert to datetime-local format
      
    } catch (err) {
      console.error('Error fetching pool:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch pool')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!poolId || saving) return

    try {
      setSaving(true)
      setError(null)

      const updateData = {
        name: name.trim(),
        description: description.trim(),
        status,
        endDate: endDate ? new Date(endDate).toISOString() : undefined,
        maxParticipants,
        entryFee,
        pickDeadline: pickDeadline ? new Date(pickDeadline).toISOString() : null
      }

      const token = await getAccessTokenSilently()
      
      const response = await fetch(`${API_BASE_URL}/pools/${poolId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || `Failed to update pool: ${response.statusText}`)
      }

      setSuccessMessage('Pool updated successfully!')
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null)
      }, 3000)

      // Refresh pool data
      await fetchPool()

    } catch (err) {
      console.error('Error updating pool:', err)
      setError(err instanceof Error ? err.message : 'Failed to update pool')
    } finally {
      setSaving(false)
    }
  }

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (!userIsPoolAdmin) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Access Denied</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">You need pool admin privileges to edit pools.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    )
  }

  if (error && !pool) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 rounded-md p-4">
          <div className="text-sm text-red-700 dark:text-red-400">{error}</div>
        </div>
      </div>
    )
  }

  if (!pool) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Pool not found</h1>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <Link 
            to={`/admin/pools/football/${poolId}`}
            className="mr-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <ArrowLeftIcon className="w-6 h-6" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Edit Pool</h1>
            <p className="mt-1 text-lg text-gray-600 dark:text-gray-300">{pool.name}</p>
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 bg-green-50 dark:bg-green-900/50 border border-green-200 dark:border-green-800 rounded-md p-4">
            <div className="text-sm text-green-700 dark:text-green-400">{successMessage}</div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 rounded-md p-4">
            <div className="text-sm text-red-700 dark:text-red-400">{error}</div>
          </div>
        )}
      </div>

      {/* Edit Form */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Pool Name *
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                required
                disabled={saving}
              />
            </div>

            <div className="md:col-span-2">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Description
              </label>
              <textarea
                id="description"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                disabled={saving}
              />
            </div>
          </div>

          {/* Pool Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Status
              </label>
              <select
                id="status"
                value={status}
                onChange={(e) => setStatus(e.target.value as 'active' | 'completed' | 'cancelled')}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                disabled={saving}
              >
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div>
              <label htmlFor="maxParticipants" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Max Participants
              </label>
              <input
                type="number"
                id="maxParticipants"
                min="1"
                max="1000"
                value={maxParticipants}
                onChange={(e) => setMaxParticipants(parseInt(e.target.value) || 100)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                disabled={saving}
              />
            </div>

            <div>
              <label htmlFor="entryFee" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Entry Fee ($)
              </label>
              <input
                type="number"
                id="entryFee"
                min="0"
                step="0.01"
                value={entryFee}
                onChange={(e) => setEntryFee(parseFloat(e.target.value) || 0)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                disabled={saving}
              />
            </div>

            <div>
              <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                End Date
              </label>
              <input
                type="date"
                id="endDate"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                disabled={saving}
              />
            </div>
          </div>

          {/* Pick Deadline */}
          <div>
            <label htmlFor="pickDeadline" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              <ClockIcon className="w-4 h-4 inline mr-1" />
              Pick Deadline
            </label>
            <input
              type="datetime-local"
              id="pickDeadline"
              value={pickDeadline}
              onChange={(e) => setPickDeadline(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              disabled={saving}
            />
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              After this deadline, participants cannot submit or change their picks. Leave empty for no deadline.
            </p>
          </div>

          {/* Current Info */}
          <div className="border-t border-gray-200 dark:border-gray-600 pt-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Current Pool Info</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="text-center">
                <UsersIcon className="w-6 h-6 mx-auto text-blue-500 mb-1" />
                <div className="font-semibold text-gray-900 dark:text-white">{pool.currentParticipants}</div>
                <div className="text-gray-500 dark:text-gray-400">Participants</div>
              </div>
              <div className="text-center">
                <CurrencyDollarIcon className="w-6 h-6 mx-auto text-green-500 mb-1" />
                <div className="font-semibold text-gray-900 dark:text-white">${pool.entryFee}</div>
                <div className="text-gray-500 dark:text-gray-400">Entry Fee</div>
              </div>
              <div className="text-center">
                <CalendarIcon className="w-6 h-6 mx-auto text-purple-500 mb-1" />
                <div className="font-semibold text-gray-900 dark:text-white">{formatDateTime(pool.createdAt)}</div>
                <div className="text-gray-500 dark:text-gray-400">Created</div>
              </div>
              <div className="text-center">
                <ClockIcon className="w-6 h-6 mx-auto text-orange-500 mb-1" />
                <div className="font-semibold text-gray-900 dark:text-white">
                  {pool.pickDeadline ? formatDateTime(pool.pickDeadline) : 'No deadline'}
                </div>
                <div className="text-gray-500 dark:text-gray-400">Pick Deadline</div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-600">
            <Link
              to={`/admin/pools/football/${poolId}`}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
